import { Availability, FileReference } from "~/entities/baseEntities";

/**
 * A flash promotion is presentation only: a timed countdown card that redeems a
 * shared voucher code when tapped. The promotion behind it owns the reward and
 * the minimum spend, and the engine applies the discount.
 *
 * `minimumSpend` is projected by the server at read time rather than stored, so
 * the copy can never promise something the engine will not honour.
 */
export type FlashPromotion = {
  id: string;
  code: string;
  name: string;
  active: boolean;

  siteIds: string[];
  orderTypeCodes: string[];

  appliesFrom: string | null;
  appliesUntil: string | null;
  availability: Availability[];

  promotionCode: string;
  voucherId: string;
  voucherUnlockCode: string;

  requiresEmptyOrder: boolean;
  delaySeconds: number;
  displayDurationSeconds: number;

  header: string;
  message: string;
  successHeader: string;
  successMessage: string;
  image: FileReference | null;

  /** cents, projected from the promotion's requiredPrice */
  minimumSpend: number;
};

/**
 * Expiring is not the same as declining. A card can count down while the
 * customer is reading the menu, and a short campaign gives them seconds to
 * notice it, so a miss is counted rather than banked as a decision — but it
 * should not nag on every navigation forever either.
 */
export const MAX_EXPIRIES = 3;

export type FlashPromotionDecisionInput = {
  campaign: FlashPromotion | null;
  /** Client-only: nothing renders until hydration */
  mounted: boolean;
  /** Dismissed on this page, before any reload */
  dismissed: boolean;
  readonlyCart: boolean;
  promoCode: string | null;
  redemptionKey: string | null;
  cartLineCount: number;
  /** Campaign ids the customer dismissed or redeemed this session */
  dismissedIds: string[];
  /** Campaign id -> how many times it counted down unengaged */
  expiryCounts: Record<string, number>;
  /** Where the countdown is up to */
  phase: "waiting" | "visible" | "expired";
};

export type FlashPromotionDecision = {
  /** Passes every non-timing gate, so the delay timer may run */
  eligible: boolean;
  /** Eligible AND the countdown says now */
  show: boolean;
  /**
   * Why it is not showing. Null when it is.
   *
   * Deliberately returned from the same function that decides, rather than
   * derived alongside it: two implementations of one rule set drift, and a
   * diagnostic that lies is worse than no diagnostic at all.
   */
  reason: string | null;
};

export const decideFlashPromotion = ({
  campaign,
  mounted,
  dismissed,
  readonlyCart,
  promoCode,
  redemptionKey,
  cartLineCount,
  dismissedIds,
  expiryCounts,
  phase,
}: FlashPromotionDecisionInput): FlashPromotionDecision => {
  const no = (reason: string): FlashPromotionDecision => ({
    eligible: false,
    show: false,
    reason,
  });

  if (!campaign) {
    return no("no campaign returned for this site and order type");
  }
  if (!mounted) {
    return no("not hydrated yet");
  }
  if (dismissed) {
    return no("dismissed");
  }
  if (readonlyCart) {
    return no("cart is read-only (payment page, or the order is placed)");
  }
  // Applying a promo code replaces any existing one, so a customer who already
  // has a code or a loyalty reward must never be offered this — we would be
  // silently destroying something they chose
  if (promoCode) {
    return no(
      `a promo code is already on the order (${promoCode}) — applying another would replace it`
    );
  }
  if (redemptionKey) {
    return no("a loyalty redemption is already on the order");
  }
  if (dismissedIds.includes(campaign.id)) {
    return no(
      "dismissed or redeemed this session — add ?flashReset to the URL to see it again"
    );
  }
  if ((expiryCounts[campaign.id] ?? 0) >= MAX_EXPIRIES) {
    return no(
      `expired ${MAX_EXPIRIES} times without being tapped — add ?flashReset to the URL to see it again`
    );
  }
  if (campaign.requiresEmptyOrder && cartLineCount > 0) {
    return no("campaign requires an empty cart and the cart has items");
  }

  // Eligible from here — anything left is the countdown
  if (phase === "waiting") {
    return {
      eligible: true,
      show: false,
      reason: `waiting out delaySeconds (${campaign.delaySeconds}s)`,
    };
  }
  if (phase === "expired") {
    return { eligible: true, show: false, reason: "countdown finished" };
  }

  return { eligible: true, show: true, reason: null };
};

const MINIMUM_SPEND_TOKEN = /\{minimumSpend\}/g;

/**
 * Success copy may reference the minimum spend, so the operator does not have to
 * retype it and get it wrong when the promotion changes.
 */
export const formatSuccessMessage = (
  message: string,
  minimumSpendLabel: string
): string => message.replace(MINIMUM_SPEND_TOKEN, minimumSpendLabel);

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(Math.round(value), min), max);

export const clampDelaySeconds = (value: number) => clamp(value, 0, 300);

/** Floor of 5s: a shorter card would vanish before it could be read */
export const clampDurationSeconds = (value: number) => clamp(value, 5, 300);
